import React, { useState, useMemo } from 'react';
import ReinnoireCard from '../Shared/ReinnoireCard';
import DateField from '../Shared/DateField';
import ProceduraFileSection from '../Shared/ProceduraFileSection';
import { FilterPill } from '../Shared/FilterPill';
import './Tracker.css';

/* ── Nice classes (shared) ── */
const CLASE_NISA = [
  [1,'Produse chimice pentru industrie si stiinta'],
  [2,'Vopsele, lacuri, coloranti, rasini'],
  [3,'Cosmetice, parfumerie, produse de curatare'],
  [4,'Uleiuri si grasimi industriale, combustibili, lumanari'],
  [5,'Produse farmaceutice, de uz medical si veterinar'],
  [6,'Metale comune, materiale de constructie metalice'],
  [7,'Masini, masini-unelte, motoare (nu pt. vehicule)'],
  [8,'Scule si instrumente de mana actionate manual'],
  [9,'Aparate stiintifice, electrice, software, IT'],
  [10,'Aparate si instrumente medicale, chirurgicale, dentare'],
  [11,'Aparate de iluminat, incalzire, racire, gatit, ventilatie'],
  [12,'Vehicule; aparate de locomotie terestra, aeriana, navala'],
  [13,'Arme de foc, munitie, explozibili, artificii'],
  [14,'Metale pretioase, bijuterii, ceasuri'],
  [15,'Instrumente muzicale'],
  [16,'Hartie, carton, tiparituri, papetarie'],
  [17,'Cauciuc, materiale plastice semiprelucrate, izolatoare'],
  [18,'Piele si imitatii, bagaje, selarie, umbrele'],
  [19,'Materiale de constructie nemetalice'],
  [20,'Mobilier, oglinzi, rame; produse din lemn, pluta'],
  [21,'Ustensile si recipiente de menaj si bucatarie, sticlarie'],
  [22,'Franghii, plase, corturi, fibre textile brute'],
  [23,'Fire de uz textil'],
  [24,'Tesaturi si produse textile, lenjerie de casa'],
  [25,'Imbracaminte, incaltaminte, articole pt. acoperirea capului'],
  [26,'Dantele, broderii, panglici, nasturi, accesorii par'],
  [27,'Covoare, presuri, linoleum, tapet'],
  [28,'Jocuri, jucarii, articole de sport, decoratiuni Craciun'],
  [29,'Carne, peste, lactate, conserve, uleiuri alimentare'],
  [30,'Cafea, ceai, faina, paine, patiserie, condimente'],
  [31,'Produse agricole si horticole brute, animale vii, plante'],
  [32,'Bere, ape minerale, bauturi nealcoolice, sucuri'],
  [33,'Bauturi alcoolice (cu exceptia berii)'],
  [34,'Tutun, articole pentru fumatori, chibrituri'],
  [35,'Publicitate, management afaceri, comert cu amanuntul/online'],
  [36,'Servicii financiare, asigurari, imobiliare'],
  [37,'Constructii, reparatii, servicii de instalatii'],
  [38,'Telecomunicatii'],
  [39,'Transport, ambalare si depozitare, organizare calatorii'],
  [40,'Prelucrarea si tratarea materialelor'],
  [41,'Educatie, instruire, divertisment, activitati sportive/culturale'],
  [42,'Servicii stiintifice si tehnologice, IT, design, software'],
  [43,'Servicii de alimentatie publica, cazare temporara'],
  [44,'Servicii medicale, veterinare, igiena, infrumusetare'],
  [45,'Servicii juridice, de securitate, servicii personale'],
];

/* ── Supported offices ── */
export const OFICII_INT = [
  { value: 'GB', label: 'GB — Regatul Unit (UKIPO)' },
  { value: 'US', label: 'US — Statele Unite (USPTO)' },
  { value: 'CA', label: 'CA — Canada (CIPO)' },
  { value: 'AU', label: 'AU — Australia (IP Australia)' },
  { value: 'CH', label: 'CH — Elvetia (IGE/IPI)' },
  { value: 'JP', label: 'JP — Japonia (JPO)' },
  { value: 'CN', label: 'CN — China (CNIPA)' },
  { value: 'BR', label: 'BR — Brazilia (INPI)' },
  { value: 'IN', label: 'IN — India (CGPDTM)' },
  { value: 'MX', label: 'MX — Mexic (IMPI)' },
  { value: 'KR', label: 'KR — Coreea de Sud (KIPO)' },
  { value: 'IL', label: 'IL — Israel (ILPO)' },
  { value: 'MD', label: 'MD — Moldova (AGEPI)' },
  { value: 'PE', label: 'PE — Peru (INDECOPI)' },
  { value: 'alt', label: 'Alt oficiu…' },
];

/* ── Helpers ── */
const todayMid = () => { const d = new Date(); d.setHours(0,0,0,0); return d; };
const parseISO = (s) => (s ? new Date(s + 'T00:00:00') : null);
const addYears  = (d, y) => { const x = new Date(d); x.setFullYear(x.getFullYear() + y); return x; };
const addMonths = (d, m) => { const x = new Date(d); x.setMonth(x.getMonth() + m); return x; };
const fmtRO = (d) => d ? d.toLocaleDateString('ro-RO', { day:'2-digit', month:'2-digit', year:'numeric' }) : '—';
const daysLeft = (d) => d ? Math.ceil((d - todayMid()) / 86400000) : null;

/* ── emptyRecord export ── */
export function emptyRecord() {
  return {
    denumire: '',
    tip: 'verbala',
    color: 'abn',
    clase: {},
    viennaCode: '',

    oficiu: 'GB',
    altOficiuNume: '',   // shown when oficiu === 'alt'

    numCerere: '',       // application / serial number
    numar: '',           // registration number

    dataDepunere: '',
    dataPublicare: '',
    dataOpozitieEnd: '', // opposition period end (usually auto-calculated but allow override)
    dataInregistrare: '',
    dataExpirare: '',    // usually 10 years; CIPO 10 years from reg; USPTO 10 years

    certificatPrimit: false,
    marcaInregistrata: false,
    transmisClient: false,

    // USPTO-specific
    bazaUspto: '1a',         // 1(a) use in commerce | 1(b) intent to use | 44e foreign reg | 44d foreign app
    firstUseAnywhere: '',
    firstUseCommerce: '',
    noticeOfAllowance: '',   // NOA date (1(b) basis only)
    statementOfUseFiled: false,
    section8Filed: false,    // Declaration of Use/Excusable Nonuse (years 5-6)
    section15Filed: false,   // Incontestability (optional, after 5 years)

    // CIPO-specific
    allowanceDate: '',               // date of allowance
    declarationOfUseFiled: false,    // Declaration of Use required within 3 years of allowance

    taxeOficiale: '',    // official fees (currency depends on office)
    observatii: '',
  };
}

/* ── Opposition period in months by office ── */
function opozitieMonths(oficiu) {
  if (oficiu === 'US') return 1;   // 30 days
  if (oficiu === 'CA') return 2;
  return 2;                         // GB, AU, most others: 2 months
}

/* ── Renewal term by office ── */
function renewalYears(oficiu) {
  if (oficiu === 'CA') return 10; // CA moved to 10y in 2019
  return 10;
}

/* ── deriveStatus ── */
function deriveStatus(r) {
  const oficiu = r.oficiu;
  const isUS = oficiu === 'US';
  const isCA = oficiu === 'CA';

  const dataPublicare = parseISO(r.dataPublicare);
  const opEnd = dataPublicare ? addMonths(dataPublicare, opozitieMonths(oficiu)) : null;
  const dataInreg   = parseISO(r.dataInregistrare);
  const dataDepunere = parseISO(r.dataDepunere);
  const expirare    = dataDepunere ? addYears(dataDepunere, renewalYears(oficiu)) : (parseISO(r.dataExpirare) || null);
  const noaDate     = parseISO(r.noticeOfAllowance);
  const allowDate   = parseISO(r.allowanceDate);
  const declaDeadline = allowDate ? addYears(allowDate, 3) : null; // CIPO: 3 years from allowance
  const sec8Due     = dataInreg ? addYears(dataInreg, 6) : null;   // USPTO: section 8 due between 5-6 years

  // Already-registered mark (imported / long-registered).
  const registered = !!(r.marcaInregistrata || r.dataInregistrare);

  /* Timeline steps */
  const steps = [
    { key:'depunere', label:'Cerere depusa', done: registered || (!!r.numCerere && !!r.dataDepunere), date: parseISO(r.dataDepunere) },
    { key:'publicare', label:'Publicare / examinare', done: registered || !!r.dataPublicare, date: dataPublicare },
  ];

  if (isUS && r.bazaUspto === '1b') {
    steps.push({ key:'noa', label:'Notice of Allowance emis', done: registered || !!r.noticeOfAllowance, date: noaDate });
    steps.push({ key:'sou', label:'Statement of Use depus', done: registered || !!r.statementOfUseFiled, date: null });
  }

  if (isCA) {
    steps.push({ key:'allowance', label:'Allowance emis', done: registered || !!r.allowanceDate, date: allowDate });
    steps.push({ key:'decla', label:'Declaration of Use depusa', done: registered || !!r.declarationOfUseFiled, date: declaDeadline });
  }

  steps.push({ key:'inreg', label:'Inregistrata', done: registered, date: dataInreg });
  steps.push({ key:'certificat', label:'Certificat primit', done: !!r.certificatPrimit, date: null });
  steps.push({ key:'client', label:'Transmis client', done: !!r.transmisClient, date: null });

  /* Alert */
  let alert;
  if (!r.numCerere) {
    alert = { label: 'In pregatire — numar cerere neintrodus', sev: 'neutral' };
  } else if (!r.dataPublicare) {
    alert = { label: 'Asteptare publicare / examinare', sev: 'info' };
  } else if (opEnd && daysLeft(opEnd) > 0 && !r.marcaInregistrata) {
    alert = { label: `Termen opozitie — ${opozitieMonths(oficiu)} luni de la publicare`, date: opEnd, daysLeft: daysLeft(opEnd), sev: daysLeft(opEnd) <= 14 ? 'act' : 'watch' };
  } else if (isUS && r.bazaUspto === '1b' && !r.noticeOfAllowance) {
    alert = { label: 'Asteptare Notice of Allowance (USPTO)', sev: 'info' };
  } else if (isUS && r.bazaUspto === '1b' && r.noticeOfAllowance && !r.statementOfUseFiled) {
    const souDeadline = noaDate ? addMonths(noaDate, 6) : null;
    alert = { label: 'Statement of Use de depus in 6 luni de la NOA', date: souDeadline, daysLeft: souDeadline ? daysLeft(souDeadline) : null, sev: 'act' };
  } else if (isCA && r.allowanceDate && !r.declarationOfUseFiled) {
    alert = { label: 'Declaration of Use de depus in 3 ani de la Allowance', date: declaDeadline, daysLeft: declaDeadline ? daysLeft(declaDeadline) : null, sev: declaDeadline && daysLeft(declaDeadline) <= 180 ? 'act' : 'watch' };
  } else if (!registered) {
    alert = { label: 'Asteptare inregistrare', sev: 'info' };
  } else if (isUS && !r.section8Filed && sec8Due) {
    alert = { label: 'Section 8 Declaration (Folosinta) de depus (ani 5–6)', date: sec8Due, daysLeft: daysLeft(sec8Due), sev: daysLeft(sec8Due) <= 365 ? 'act' : 'watch' };
  } else if (r.certificatPrimit && !r.transmisClient) {
    alert = { label: 'De transmis certificatul catre client', sev: 'act' };
  } else {
    alert = { label: 'Marca inregistrata', sev: 'done' };
  }

  /* Expiry warning overrides for registered marks */
  if (expirare && (alert.sev === 'done' || alert.sev === 'act')) {
    const dl = daysLeft(expirare);
    if (dl !== null && dl <= 365) {
      alert = { label: dl < 0 ? 'Inregistrare expirata — reinnoire necesara' : 'Inregistrare expira curand — reinnoire necesara', date: expirare, daysLeft: dl, sev: dl < 0 ? 'overdue' : 'watch' };
    }
  }

  return { steps, alert, expirare, opEnd, sec8Due, declaDeadline };
}

/* ════════════════════════ Component ════════════════════════ */
export default function MarcaInternationalaTracker({ data, onChange, procId, showToast, pendingFiles, onPendingFilesChange }) {
  const [r, setR] = useState(data);

  const set = (patch) => {
    const next = { ...r, ...patch };
    setR(next);
    onChange(next);
  };

  const status   = useMemo(() => deriveStatus(r), [r]);
  const isVerbala = r.tip === 'verbala';
  const isUS      = r.oficiu === 'US';
  const isCA      = r.oficiu === 'CA';
  const isAlt     = r.oficiu === 'alt';

  const oficiuLabel = isAlt
    ? (r.altOficiuNume || 'Alt oficiu')
    : OFICII_INT.find(o => o.value === r.oficiu)?.label || r.oficiu;

  const toggleClasa  = (nr) => { const c = { ...r.clase }; if (c[nr] !== undefined) delete c[nr]; else c[nr] = ''; set({ clase: c }); };
  const setTermeni   = (nr, val) => set({ clase: { ...r.clase, [nr]: val } });

  const numCerarePlaceholder = isUS ? 'ex: 97/123456' : isCA ? 'ex: 1234567' : 'ex: UK00003012345';
  const numInregPlaceholder  = isUS ? 'ex: 7654321' : isCA ? 'ex: TMA123456' : 'ex: UK00003012345';
  const taxaCurrency = isUS ? 'USD' : isCA ? 'CAD' : 'GBP';

  return (
    <div className="tracker">
      <AlertBanner status={status} />
      <Timeline steps={status.steps} />

      {/* Identificare */}
      <TrackerCard title="Identificarea marcii">
        <div className="f-row">
          <div className="field">
            <label>Denumire marca</label>
            <input type="text" value={r.denumire} onChange={e => set({ denumire: e.target.value })} placeholder="ex: Aurora Style" />
          </div>
          <div className="field">
            <label>Tip marca</label>
            <FilterPill value={r.tip} onChange={e => set({ tip: e.target.value })}>
              <option value="verbala">Verbala</option>
              <option value="combinata">Combinata (cuvant + imagine)</option>
              <option value="figurativa">Figurativa (logo/imagine)</option>
              <option value="stilizata">Caractere Stilizate</option>
              <option value="tridimensionala">Tridimensionala (3D)</option>
              <option value="sonora">Sonora</option>
              <option value="alt">Alt tip</option>
            </FilterPill>
          </div>
        </div>
        {!isVerbala && (
          <div className="field">
            <label>Fisiere de reprezentare <span className="f-hint">— logo, imagine, fisier 3D etc.</span></label>
            <ProceduraFileSection procId={procId} showToast={showToast} pendingFiles={pendingFiles} onPendingFilesChange={onPendingFilesChange} />
          </div>
        )}
        {!isVerbala && (
          <div className="field">
            <label>Coduri Vienna <span className="f-hint">— separate prin virgula, ex: 05.03.13</span></label>
            <input type="text" value={r.viennaCode || ''} onChange={e => set({ viennaCode: e.target.value })} className="mono" placeholder="ex: 05.03.13, 26.04.07" />
          </div>
        )}
        <div className="field">
          <label>Culoare {isVerbala && <span className="f-hint">— marca verbala este standard (alb-negru)</span>}</label>
          <SegControl value={isVerbala ? 'abn' : r.color} disabled={isVerbala} onChange={v => set({ color: v })} options={[['abn','Alb-negru'],['color','Color']]} />
        </div>
      </TrackerCard>

      {/* Oficiu */}
      <TrackerCard title="Oficiu national">
        <div className="f-row">
          <div className="field">
            <label>Oficiu de inregistrare</label>
            <FilterPill value={r.oficiu} onChange={e => set({ oficiu: e.target.value, altOficiuNume: '', numCerere:'', numar:'', bazaUspto:'1a', allowanceDate:'', noticeOfAllowance:'' })}>
              {OFICII_INT.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </FilterPill>
          </div>
          {isAlt && (
            <div className="field">
              <label>Nume oficiu</label>
              <input type="text" value={r.altOficiuNume} onChange={e => set({ altOficiuNume: e.target.value })} placeholder="ex: INPI, IPO, SIPO…" />
            </div>
          )}
        </div>
        {isUS && (
          <div className="field">
            <label>Baza cererii (USPTO)</label>
            <FilterPill value={r.bazaUspto} onChange={e => set({ bazaUspto: e.target.value })}>
              <option value="1a">1(a) — Folosinta in comert (use in commerce)</option>
              <option value="1b">1(b) — Intentie de folosinta (intent to use)</option>
              <option value="44e">44(e) — Inregistrare straina existenta</option>
              <option value="44d">44(d) — Cerere straina prioritara</option>
            </FilterPill>
          </div>
        )}
        {isUS && (r.bazaUspto === '1a' || r.bazaUspto === '1b') && (
          <div className="f-row">
            <div className="field">
              <label>Prima folosinta oriunde <span className="f-hint">(date of first use anywhere)</span></label>
              <DateField value={r.firstUseAnywhere || ''} onChange={v => set({ firstUseAnywhere: v })} />
            </div>
            <div className="field">
              <label>Prima folosinta in comert <span className="f-hint">(date of first use in commerce)</span></label>
              <DateField value={r.firstUseCommerce || ''} onChange={v => set({ firstUseCommerce: v })} />
            </div>
          </div>
        )}
      </TrackerCard>

      {/* Clase Nisa */}
      <TrackerCard title={`Clase Nisa (${Object.keys(r.clase).length} selectate)`} collapsible defaultOpen={false}>
        <p className="f-hint mb8">Bifeaza clasele si lipeste lista de produse/servicii per clasa.</p>
        <div className="clase-grid">
          {CLASE_NISA.map(([nr, titlu]) => {
            const sel = r.clase[nr] !== undefined;
            return (
              <div key={nr} className={`clasa-item${sel ? ' clasa-sel' : ''}`}>
                <label className="clasa-label">
                  <input type="checkbox" checked={sel} onChange={() => toggleClasa(nr)} />
                  <span className="clasa-nr">{String(nr).padStart(2,'0')}</span>
                  <span className="clasa-titlu">{titlu}</span>
                </label>
                {sel && (
                  <textarea
                    value={r.clase[nr]}
                    onChange={e => setTermeni(nr, e.target.value)}
                    placeholder={`Produse/servicii pt. clasa ${nr}…`}
                    rows={2}
                    className="clasa-textarea"
                  />
                )}
              </div>
            );
          })}
        </div>
      </TrackerCard>

      {/* Taxe */}
      <TrackerCard title={`Taxe oficiale (${isAlt ? r.altOficiuNume || 'oficiu' : r.oficiu})`}>
        <div className="field">
          <label>Valoare taxe oficiale de depunere</label>
          <div style={{ display:'flex', alignItems:'center', gap: 8 }}>
            <input type="number" min="0" step="any" value={r.taxeOficiale || ''} onChange={e => set({ taxeOficiale: e.target.value })} placeholder="ex: 170" style={{ maxWidth: 160 }} />
            <span style={{ fontWeight:600, color:'var(--muted)' }}>{taxaCurrency}</span>
          </div>
          {isUS && <span className="f-hint">USPTO: ~$350/clasa (TEAS Plus) sau $650/clasa (TEAS Standard) — 2024.</span>}
          {!isUS && <span className="f-hint">Verificati tarifele actualizate pe site-ul oficial al {oficiuLabel}.</span>}
        </div>
      </TrackerCard>

      {/* Depunere si monitorizare */}
      <TrackerCard title="Depunere si monitorizare">
        <div className="f-row">
          <div className="field">
            <label>Nr. cerere / serial number</label>
            <input type="text" value={r.numCerere} onChange={e => set({ numCerere: e.target.value })} placeholder={numCerarePlaceholder} className="mono" />
          </div>
          <div className="field">
            <label>Data depunerii</label>
            <DateField value={r.dataDepunere} onChange={v => set({ dataDepunere: v })} />
          </div>
        </div>

        <div className="tr-divider">Publicare si opozitie</div>

        <div className="f-row">
          <div className="field">
            <label>Data publicarii</label>
            <DateField value={r.dataPublicare} onChange={v => set({ dataPublicare: v })} />
            <span className="f-hint">
              {isUS ? 'Publicata in Official Gazette — 30 zile opozitie.' : isCA ? 'Publicata in Journal of Official Marks — 2 luni opozitie.' : 'Publicata in Trade Marks Journal — 2 luni opozitie.'}
            </span>
          </div>
          {status.opEnd && (
            <div className="field">
              <label>Sfarsit termen opozitie</label>
              <div style={{ paddingTop: 4 }}>
                <DeadlineChip label={`Opozitie (${opozitieMonths(r.oficiu)} luni)`} date={status.opEnd} act />
              </div>
            </div>
          )}
        </div>

        {/* USPTO — intent to use: NOA + Statement of Use */}
        {isUS && r.bazaUspto === '1b' && (
          <>
            <div className="tr-divider">Intent to Use — Notice of Allowance</div>
            <div className="f-row">
              <div className="field">
                <label>Data Notice of Allowance (NOA)</label>
                <DateField value={r.noticeOfAllowance || ''} onChange={v => set({ noticeOfAllowance: v })} />
                <span className="f-hint">Dupa NOA, aveti 6 luni pentru a depune Statement of Use (extensibil de pana la 5 ori).</span>
              </div>
              <div className="field">
                <label>Statement of Use depus</label>
                <label className="check-label" style={{ marginTop: 8 }}>
                  <input type="checkbox" checked={r.statementOfUseFiled || false} onChange={e => set({ statementOfUseFiled: e.target.checked })} />
                  Statement of Use a fost depus
                </label>
              </div>
            </div>
          </>
        )}

        {/* CIPO — allowance + declaration of use */}
        {isCA && (
          <>
            <div className="tr-divider">Allowance si Declaration of Use</div>
            <div className="f-row">
              <div className="field">
                <label>Data Allowance</label>
                <DateField value={r.allowanceDate || ''} onChange={v => set({ allowanceDate: v })} />
                <span className="f-hint">Declaration of Use trebuie depusa in 3 ani de la data Allowance.</span>
              </div>
              <div className="field">
                <label>Declaration of Use</label>
                {status.declaDeadline && (
                  <div style={{ paddingTop: 4, paddingBottom: 4 }}>
                    <DeadlineChip label="Termen Declaration of Use (3 ani)" date={status.declaDeadline} act />
                  </div>
                )}
                <label className="check-label">
                  <input type="checkbox" checked={r.declarationOfUseFiled || false} onChange={e => set({ declarationOfUseFiled: e.target.checked })} />
                  Declaration of Use a fost depusa
                </label>
              </div>
            </div>
          </>
        )}

        <div className="tr-divider">Inregistrare si reinnoire</div>

        <div className="f-row">
          <div className="field">
            <label>Nr. inregistrare</label>
            <input type="text" value={r.numar} onChange={e => set({ numar: e.target.value })} placeholder={numInregPlaceholder} className="mono" />
          </div>
          <div className="field">
            <label>Data inregistrarii</label>
            <DateField value={r.dataInregistrare} onChange={v => set({ dataInregistrare: v })} />
          </div>
        </div>

        <div className="f-row">
          <div className="field">
            <label>Data expirarii</label>
            <DateField value={r.dataExpirare || ''} onChange={v => set({ dataExpirare: v })} />
            <span className="f-hint">
              {isUS ? 'Reinnoire la 10 ani de la data depunerii (Section 8 + 9).' : isCA ? 'Reinnoire la 10 ani de la data depunerii.' : 'Reinnoire la 10 ani de la data depunerii.'}
            </span>
          </div>
          {status.expirare && (
            <div className="field" style={{ paddingTop: 22 }}>
              <DeadlineChip label="Expirare inregistrare" date={status.expirare} act />
            </div>
          )}
        </div>

        {/* USPTO Section 8/9 declarations */}
        {isUS && (
          <>
            <div className="tr-divider">Declaratii de mentinere (USPTO)</div>
            <div className="f-row">
              <div className="field">
                {status.sec8Due && (
                  <div style={{ marginBottom: 8 }}>
                    <DeadlineChip label="Section 8 Declaration (ani 5–6)" date={status.sec8Due} act />
                  </div>
                )}
                <label className="check-label">
                  <input type="checkbox" checked={r.section8Filed || false} onChange={e => set({ section8Filed: e.target.checked })} />
                  Section 8 (Declaration of Use) a fost depusa
                </label>
              </div>
              <div className="field">
                <label className="check-label">
                  <input type="checkbox" checked={r.section15Filed || false} onChange={e => set({ section15Filed: e.target.checked })} />
                  Section 15 (Incontestability) a fost depusa
                  <span className="f-hint" style={{ display:'block', marginLeft: 22 }}>Optional — dupa 5 ani de folosinta continua.</span>
                </label>
              </div>
            </div>
          </>
        )}

        <div className="tr-divider">Finalizare</div>

        <label className="check-label">
          <input type="checkbox" checked={r.marcaInregistrata || false} onChange={e => set({ marcaInregistrata: e.target.checked })} />
          Statusul marcii este <strong>inregistrata</strong>
        </label>
        <label className="check-label">
          <input type="checkbox" checked={r.certificatPrimit || false} onChange={e => set({ certificatPrimit: e.target.checked })} />
          Certificatul de inregistrare a fost primit
        </label>
        <label className={`check-label${!r.certificatPrimit ? ' disabled' : ''}`}>
          <input type="checkbox" disabled={!r.certificatPrimit} checked={r.transmisClient || false} onChange={e => set({ transmisClient: e.target.checked })} />
          Certificatul a fost transmis catre client
        </label>
      </TrackerCard>

            {/* ── Reinnoire ── */}
      <ReinnoireCard r={r} set={set} TrackerCard={TrackerCard} />

      {/* Observatii */}
      <TrackerCard title="Observatii interne">
        <textarea className="tr-textarea" value={r.observatii || ''} onChange={e => set({ observatii: e.target.value })} rows={3} placeholder="Note de dosar, corespondenta, instructiuni speciale…" />
      </TrackerCard>

      {/* Documente */}
      <TrackerCard title="Documente inregistrare">
        <ProceduraFileSection
          procId={procId}
          showToast={showToast}
          pendingFiles={pendingFiles}
          onPendingFilesChange={onPendingFilesChange}
          tipOptions={['Confirmare depunere', 'Cerere inregistrare', 'Certificat inregistrare', 'Notice of Allowance', 'Statement of Use', 'Declaration of Use', 'Corespondenta oficiu']}
          keyPrefix="inreg"
        />
      </TrackerCard>
    </div>
  );
}

/* ── Sub-components ── */
function AlertBanner({ status }) {
  const { alert } = status;
  return (
    <div className={`tr-alert sev-${alert.sev}`}>
      <strong>{alert.label}</strong>
      {alert.date && (
        <span className="tr-alert-date">
          Termen: {fmtRO(alert.date)}
          {typeof alert.daysLeft === 'number' && (
            alert.daysLeft < 0
              ? ` · depasit cu ${Math.abs(alert.daysLeft)} zile`
              : ` · ${alert.daysLeft} zile ramase`
          )}
        </span>
      )}
    </div>
  );
}

function Timeline({ steps }) {
  const activeIdx = steps.findIndex(s => !s.done);
  return (
    <div className="tr-timeline">
      {steps.map((s, i) => {
        const isActive = i === activeIdx;
        const done = s.done;
        return (
          <React.Fragment key={s.key}>
            {i > 0 && <span className={`tl-line${done || steps[i-1].done ? ' tl-line-done' : ''}`} />}
            <div className="tl-step">
              <span className={`tl-dot${done ? ' done' : isActive ? ' active' : ''}`}>{done ? '✓' : i + 1}</span>
              <span className={`tl-label${isActive ? ' active' : done ? ' done' : ''}`}>{s.label}</span>
              {s.date && <span className="tl-date">{fmtRO(s.date)}</span>}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function TrackerCard({ title, children, collapsible = false, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const isOpen = !collapsible || open;
  return (
    <div className="tr-card">
      <div className={`tr-card-head${collapsible ? ' tr-card-head-toggle' : ''}`} onClick={collapsible ? () => setOpen(o => !o) : undefined}>
        <span className="tr-card-title">{title}</span>
        {collapsible && <span className={`tr-card-arrow${isOpen ? ' tr-card-arrow-open' : ''}`}>›</span>}
      </div>
      {isOpen && <div className="tr-card-body">{children}</div>}
    </div>
  );
}

function SegControl({ value, onChange, options, disabled }) {
  return (
    <div className={`seg-ctrl${disabled ? ' seg-disabled' : ''}`}>
      {options.map(([v, label]) => (
        <button key={v} type="button" disabled={disabled} className={`seg-btn${value === v ? ' seg-active' : ''}`} onClick={() => !disabled && onChange(v)}>{label}</button>
      ))}
    </div>
  );
}

function DeadlineChip({ label, date, act }) {
  const dl = daysLeft(date);
  const overdue = dl < 0;
  const soon = dl >= 0 && dl <= 14;
  const cls = overdue ? 'chip-overdue' : (act && soon) ? 'chip-act' : 'chip-neutral';
  return (
    <div className={`deadline-chip ${cls}`}>
      <span>{label}</span>
      <span>{fmtRO(date)} · {overdue ? `${Math.abs(dl)}z depasit` : `${dl}z`}</span>
    </div>
  );
}
